/**
 * rAF-based плавный скролл. НЕ зависит от нативного `scroll-behavior: smooth`
 * и опции `scrollTo({behavior:'smooth'})` — анимируем scrollTop/scrollLeft сами
 * по кадрам. Нужно потому, что в части webview/встроенных браузеров нативный
 * smooth-скролл — no-op (прыжок к категории просто не срабатывал). Так работает
 * везде одинаково. Уважает prefers-reduced-motion (сразу прыгает без анимации).
 */
export function smoothScrollTo(
  el: HTMLElement | null | undefined,
  target: number,
  opts: { axis?: 'top' | 'left'; duration?: number } = {}
): void {
  if (!el) return;
  const axis = opts.axis ?? 'top';
  const duration = opts.duration ?? 400;
  const isTop = axis === 'top';

  const start = isTop ? el.scrollTop : el.scrollLeft;
  const max = isTop ? el.scrollHeight - el.clientHeight : el.scrollWidth - el.clientWidth;
  const clamped = Math.max(0, Math.min(target, max));
  const distance = clamped - start;
  if (Math.abs(distance) < 2) return;

  // КЛЮЧЕВОЕ: CSS `scroll-behavior: smooth` (класс на контейнере) перехватывает
  // даже прямое присвоение scrollTop/scrollLeft — и в части webview это no-op.
  // Форсим `auto` на время своей анимации, плавность даёт rAF, потом восстанавливаем.
  const prevBehavior = el.style.scrollBehavior;
  el.style.scrollBehavior = 'auto';
  const set = (v: number) => {
    if (isTop) el.scrollTop = v;
    else el.scrollLeft = v;
  };
  const restore = () => {
    el.style.scrollBehavior = prevBehavior;
  };

  const reduce =
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (reduce || duration <= 0) {
    set(clamped);
    restore();
    return;
  }

  // Гарантия прибытия: rAF ставится на паузу для скрытых вкладок/во многих
  // webview — тогда анимация вообще не пойдёт. Страхуемся setTimeout'ом
  // (он лишь троттлится, но срабатывает): если к duration+буфер анимация не
  // финишировала, мгновенно доводим до цели. Foreground — rAF отработает
  // первым, finish() идемпотентен.
  let finished = false;
  const finish = () => {
    if (finished) return;
    finished = true;
    set(clamped);
    restore();
  };

  const startTime = performance.now();
  // easeInOutQuad
  const ease = (t: number) => (t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2);

  const step = (now: number) => {
    if (finished) return;
    const elapsed = now - startTime;
    const t = Math.min(1, elapsed / duration);
    set(start + distance * ease(t));
    if (t < 1) requestAnimationFrame(step);
    else finish();
  };
  requestAnimationFrame(step);
  setTimeout(finish, duration + 120);
}
